import { inject, Injectable } from '@angular/core';
import { GuidelineGraphService, GuidelineService } from 'shared/domain';
import { FlowchartExportUtil, FlowchartPaperRegistryService } from 'shared/feature-flowchart';

import { GuidelineWidget, FlowchartWidget } from '@treetop/shared/model';

import { GuidelinePdfHttpService } from './services/guideline-pdf-http.service';

@Injectable()
export class GuidelinePdfExportService {
  private flowchartPaperRegistry = inject(FlowchartPaperRegistryService);
  private pdfHttpService = inject(GuidelinePdfHttpService);
  private guidelineService = inject(GuidelineService);
  private guidelineGraphService = inject(GuidelineGraphService);

  public exportGuidelineToPdf(guidelineUniqueId: string, version?: string | null): Promise<Blob> {
    return this.exportGuideline((flowchartImages, referenceNumbers, resolve, reject) => {
      this.sendPdfExportRequest(guidelineUniqueId, flowchartImages, referenceNumbers, version, resolve, reject);
    });
  }

  public exportGuidelineToHtml(guidelineUniqueId: string, version?: string | null): Promise<Blob> {
    return this.exportGuideline((flowchartImages, referenceNumbers, resolve, reject) => {
      this.sendHtmlExportRequest(guidelineUniqueId, flowchartImages, referenceNumbers, version, resolve, reject);
    });
  }

  private exportGuideline(
    sendExportRequest: (
      flowchartImages: Map<string, Blob>,
      referenceNumbers: Record<string, number>,
      resolve: (blob: Blob) => void,
      reject: (error: Error) => void,
    ) => void,
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      // Get all flowchart widgets from the guideline
      const guideline = this.guidelineService.guideline;
      if (!guideline || !guideline.widgetMap) {
        const error = new Error('Cannot export guideline: guideline data not available');
        console.error(error.message);
        reject(error);
        return;
      }

      const widgetMap = guideline.widgetMap as Record<string, GuidelineWidget>;
      const flowchartWidgets: FlowchartWidget[] = Object.values(widgetMap).filter(
        (widget: GuidelineWidget): widget is FlowchartWidget => widget?.widgetType === 'flowchart',
      );

      const flowchartImages = new Map<string, Blob>();

      const referenceNumbers = this.getReferenceNumbers();

      if (flowchartWidgets.length === 0) {
        sendExportRequest(flowchartImages, referenceNumbers, resolve, reject);
        return;
      }

      let completedCount = 0;
      const totalCount = flowchartWidgets.length;

      flowchartWidgets.forEach((widget) => {
        const widgetId = widget.uniqueId;
        if (!widgetId) {
          console.warn('Flowchart widget missing uniqueId');
          completedCount++;
          if (completedCount === totalCount) {
            sendExportRequest(flowchartImages, referenceNumbers, resolve, reject);
          }
          return;
        }

        const paper = this.flowchartPaperRegistry.get(widgetId);
        if (!paper) {
          console.warn(`Paper not found for flowchart widget ${widgetId}`);
          completedCount++;
          if (completedCount === totalCount) {
            sendExportRequest(flowchartImages, referenceNumbers, resolve, reject);
          }
          return;
        }

        FlowchartExportUtil.convertPaperToPngBlob(paper, (blob) => {
          flowchartImages.set(widgetId, blob);
          completedCount++;
          if (completedCount === totalCount) {
            sendExportRequest(flowchartImages, referenceNumbers, resolve, reject);
          }
        });
      });
    });
  }

  private getReferenceNumbers(): Record<string, number> {
    this.guidelineGraphService.recalculateReferenceNumbers();
    const orderedReferences = this.guidelineGraphService.orderedReferences();
    const referenceNumbers: Record<string, number> = {};

    orderedReferences.forEach((ref) => {
      if (ref.id && ref.referenceNumber) {
        referenceNumbers[ref.id] = ref.referenceNumber;
      }
    });

    return referenceNumbers;
  }

  private sendPdfExportRequest(
    guidelineUniqueId: string,
    flowchartImages: Map<string, Blob>,
    referenceNumbers: Record<string, number>,
    version: string | null | undefined,
    resolve: (blob: Blob) => void,
    reject: (error: Error) => void,
  ) {
    this.pdfHttpService.exportGuidelineToPdf(guidelineUniqueId, flowchartImages, referenceNumbers, version).subscribe({
      next: (pdfBlob) => {
        resolve(pdfBlob);
      },
      error: (error) => {
        console.error('Failed to export guideline as PDF:', error);
        reject(error instanceof Error ? error : new Error('Failed to export PDF'));
      },
    });
  }

  private sendHtmlExportRequest(
    guidelineUniqueId: string,
    flowchartImages: Map<string, Blob>,
    referenceNumbers: Record<string, number>,
    version: string | null | undefined,
    resolve: (blob: Blob) => void,
    reject: (error: Error) => void,
  ) {
    this.pdfHttpService.exportGuidelineToHtml(guidelineUniqueId, flowchartImages, referenceNumbers, version).subscribe({
      next: (htmlBlob) => {
        resolve(htmlBlob);
      },
      error: (error) => {
        console.error('Failed to export guideline as HTML:', error);
        reject(error instanceof Error ? error : new Error('Failed to export HTML'));
      },
    });
  }
}
